import * as client from "_graphql-types";
import * as types from "./types";

type Sdk = ReturnType<typeof client.getSdk>;

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

/*********************************************************************/
async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.InvestmentFdpLoginsSortEnum>
) {
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
  } = params;

  const {
    investmentFDPLoginsList: { items: data, total },
  } = await sdk.getListInvestmentFDPLogins({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter,
  });

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function getOne(sdk: Sdk, params: types.GetOneParams) {
  const id = Number(params.id);
  const { investmentFDPLogins: data } = await sdk.getOneInvestmentFDPLogins({
    id,
  });

  return {
    data,
  };
}

/*********************************************************************/
async function getMany(sdk: Sdk, params: types.GetManyParams) {
  const ids = params.ids.map(id => Number(id));

  const { investmentFDPLoginsMany: data } =
    await sdk.getManyInvestmentFDPLogins({
      ids,
    });

  return {
    data,
  };
}

/*********************************************************************/
async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.InvestmentFdpLoginsSortEnum>
) {
  const id = params.id;
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
    target,
  } = params;

  const {
    investmentFDPLoginsList: { items: data, total },
  } = await sdk.getListInvestmentFDPLogins({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter: { ...filter, [target]: id },
  });

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function create(
  sdk: Sdk,
  params: types.CreateParams<
    NonNullable<client.GetOneInvestmentFdpLoginsQuery["investmentFDPLogins"]>
  >
) {
  const { data } = params;

  const input: client.CreateInvestmentFdpLoginsMutationVariables["input"] = {
    investmentId: data.investmentId ?? data.investment?.id,
    fdpLoginId: data.fdpLoginId,
    fdpWebsiteId: data.fdpWebsiteId,
  };

  const {
    addInvestmentFDPLogins: { id },
  } = await sdk.createInvestmentFDPLogins({
    input,
  });
  return await getOne(sdk, { id: id! });
}

/*********************************************************************/
async function update(
  sdk: Sdk,
  params: types.UpdateParams<
    NonNullable<client.GetOneInvestmentFdpLoginsQuery["investmentFDPLogins"]>
  >
) {
  const id = Number(params.id);
  const { data } = params;

  const input: client.UpdateInvestmentFdpLoginsMutationVariables["input"] = {
    investmentId: data.investmentId ?? data.investment?.id,
    fdpLoginId: data.fdpLoginId,
    fdpWebsiteId: data.fdpWebsiteId,
  };

  await sdk.updateInvestmentFDPLogins({
    id,
    input,
  });
  return await getOne(sdk, { id });
}

/*********************************************************************/
export const investmentFDPLogins = types.dataProvider({
  getList,
  getOne,
  getMany,
  getManyReference,
  create,
  update,
});
